import { useMemo, useState } from 'react';
import { AppButton } from '../Button/_index';
import { onHighlight, onPrettyJson } from './_highlight';
import { FaCheck, FaChevronDown, FaCopy, FaEye, FaEyeSlash } from 'react-icons/fa';

const BLOCK = 'LStudio__app_code';

/*
    🔥 BLOQUE DE CÓDIGO

    Para mostrar la respuesta de una API, un fragmento de configuración,
    un log o una clave. En cualquier panel de administración aparece.

    ⚠️ COPIAR TIENE FALLBACK

    navigator.clipboard solo existe en contextos seguros: en http, o
    en una IP de red local durante el desarrollo, no está. Sin el
    fallback, el botón no hace absolutamente nada y parece roto.

    ⚠️ isSecret NO ES SEGURIDAD

    Tapa el valor para que no quede expuesto en una captura o en un
    compartir pantalla. El dato ya está en el DOM: quien abra las
    herramientas lo ve igual. Es contra miradas, no contra atacantes.

    ⚠️ EL COLAPSO MIDE EN LÍNEAS, NO EN PÍXELES

    Con max-height fijo, un bloque de 8 líneas cortas se colapsa igual
    que uno de 200. Se cuenta el salto de línea y recién ahí se decide.
*/

export const AppCodeBlock = ({
    code = '', language = 'json',  // json | js | text
    title = null, hasLineNumbers = true, hasCopy = true,
    isSecret = false, maxLines = 18, isPretty = null,  // por defecto formatea si es json
    className = '',
}) => {

    const [useCopied, setCopied] = useState(false);
    const [useOpen, setOpen] = useState(false);
    const [useRevealed, setRevealed] = useState(!isSecret);

    const MEMO__TEXT = useMemo(() => {
        const SHOULD = isPretty ?? (language === 'json');
        return SHOULD ? onPrettyJson(code) : String(code ?? '');
    }, [code, language, isPretty]);

    const MEMO__LINES = useMemo(() => MEMO__TEXT.split('\n'), [MEMO__TEXT]);

    const IS_LONG = MEMO__LINES.length > maxLines;

    const MEMO__HTML = useMemo(
        () => onHighlight(useRevealed ? MEMO__TEXT : '•'.repeat(Math.min(MEMO__TEXT.length, 48)), language),
        [MEMO__TEXT, language, useRevealed],
    );

    const onCopy = async () => {

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(MEMO__TEXT);
            } else {
                /*
                    🔥 Fallback para http y navegadores viejos: el textarea
                       tiene que estar EN el documento y visible para el
                       navegador, por eso se manda fuera de pantalla en vez
                       de esconderlo con display:none.
                */
                const AREA = document.createElement('textarea');
                AREA.value = MEMO__TEXT;
                AREA.style.position = 'fixed';
                AREA.style.left = '-9999px';
                document.body.appendChild(AREA);
                AREA.select();
                document.execCommand('copy');
                document.body.removeChild(AREA);
            }

            setCopied(true);
            setTimeout(() => setCopied(false), 1600);

        } catch {
            setCopied(false);
        }
    };

    return (
        <div className={`${BLOCK} ${className}`} data-open={useOpen} data-long={IS_LONG}
            style={{ '--LS__code_lines': maxLines }} >

            {
                (title || hasCopy || isSecret) && (
                    <header className={`${BLOCK}__head`}>

                        <span className={`${BLOCK}__title`}>

                            {title ?? language}

                            <em className={`${BLOCK}__lang`}>{MEMO__LINES.length} líneas</em>

                        </span>

                        <div className={`${BLOCK}__tools`}>

                            {
                                isSecret && (
                                    <AppButton    variant="ghost" tone="neutral" size="sm" isIconOnly
                                        icon={useRevealed ? <FaEyeSlash /> : <FaEye />}  aria-label={useRevealed ? 'Ocultar' : 'Mostrar'}
                                        onClick={() => setRevealed(!useRevealed)}
                                    />
                                )
                            }

                            {
                                hasCopy && (
                                    <AppButton  variant="ghost"   tone={useCopied ? 'ok' : 'neutral'}
                                        size="sm"   icon={useCopied ? <FaCheck /> : <FaCopy />} onClick={onCopy}  >
                                        {useCopied ? 'Copiado' : 'Copiar'}
                                    </AppButton>
                                )
                            }

                        </div>

                    </header>
                )
            }

            <div className={`${BLOCK}__body`}>

                {
                    hasLineNumbers && useRevealed && (
                        <span className={`${BLOCK}__gutter`} aria-hidden="true">
                            {MEMO__LINES.map((_, i) => <i key={i}>{i + 1}</i>)}
                        </span>
                    )
                }

                <pre className={`${BLOCK}__pre`}>
                    {/*
                        ⚠️ El HTML viene de onHighlight, que escapa el texto
                           ANTES de tokenizar. Es la única entrada posible.
                    */}
                    <code dangerouslySetInnerHTML={{ __html: MEMO__HTML }} />
                </pre>

            </div>

            {
                IS_LONG && (
                    <button type="button" className={`${BLOCK}__toggle`} onClick={() => setOpen(!useOpen)}>

                        <FaChevronDown aria-hidden="true" />

                        {useOpen ? 'Mostrar menos' : `Mostrar las ${MEMO__LINES.length} líneas`}

                    </button>
                )
            }

        </div>
    );
};
